/**
 * Maintenance Window API service (Story 1.5)
 *
 * Calls backend endpoints from Story 1.2:
 * - GET  /maintenance/status     — public status
 * - POST /maintenance/activate   — activate maintenance
 * - POST /maintenance/deactivate — deactivate maintenance
 * - GET  /maintenance/history    — admin history
 */
import { api } from "./api";
import type {
  MaintenanceStateResponse,
  MaintenanceActivateRequest,
  MaintenanceActivateResponse,
  MaintenanceDeactivateRequest,
  MaintenancePreviewResponse,
  MaintenanceTemplate,
  MaintenanceWindowRecord,
  MaintenanceNotifyRequest,
  MaintenanceOptInResponse,
  MaintenanceModulesResponse,
  MaintenanceHistoryListResponse,
  MaintenanceHistoryFilters,
  TimezonePreviewRequest,
  TimezonePreviewResponse,
  DependencyAnalysisResponse,
} from "@/types/maintenance-window";

const BASE = "/maintenance";

export const maintenanceWindowApi = {
  getStatus: async (): Promise<MaintenanceStateResponse> => {
    const response = await api.get<MaintenanceStateResponse>(`${BASE}/status`);
    return response.data;
  },

  activate: async (data: MaintenanceActivateRequest): Promise<MaintenanceActivateResponse> => {
    const response = await api.post<MaintenanceActivateResponse>(`${BASE}/activate`, data);
    return response.data;
  },

  deactivate: async (data?: MaintenanceDeactivateRequest): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`${BASE}/deactivate`, data || {});
    return response.data;
  },

  getTemplates: async (): Promise<MaintenanceTemplate[]> => {
    const response = await api.get<MaintenanceTemplate[]>(`${BASE}/templates`);
    return response.data;
  },

  getPreview: async (): Promise<MaintenancePreviewResponse> => {
    const response = await api.get<MaintenancePreviewResponse>(`${BASE}/preview`);
    return response.data;
  },

  getHistory: async (
    params: MaintenanceHistoryFilters & { limit?: number; offset?: number } = {}
  ): Promise<MaintenanceHistoryListResponse> => {
    const response = await api.get<MaintenanceHistoryListResponse>(`${BASE}/history`, {
      params: { limit: 20, offset: 0, ...params },
    });
    return response.data;
  },

  getDrafts: async (): Promise<
    Array<{ route: string; data: Record<string, unknown>; saved_at: string }>
  > => {
    const response = await api.get(`${BASE}/drafts`);
    return response.data;
  },

  deleteDraft: async (routeHash: string): Promise<void> => {
    await api.delete(`${BASE}/drafts/${routeHash}`);
  },

  deleteAllDrafts: async (): Promise<void> => {
    await api.delete(`${BASE}/drafts`);
  },

  /** Story 4.2: Send maintenance notification emails */
  sendNotification: async (
    data: MaintenanceNotifyRequest
  ): Promise<{ sent_to: number; status: string }> => {
    const response = await api.post<{ sent_to: number; status: string }>(`${BASE}/notify`, data);
    return response.data;
  },

  /** Story 4.2: Preview scheduled time in multiple timezones */
  getTimezonePreview: async (data: TimezonePreviewRequest): Promise<TimezonePreviewResponse> => {
    const response = await api.post<TimezonePreviewResponse>(`${BASE}/timezone-preview`, data);
    return response.data;
  },

  /** Story 5.1: Get maintenance modules grouped by zone */
  getModules: async (): Promise<MaintenanceModulesResponse> => {
    const response = await api.get<MaintenanceModulesResponse>(`${BASE}/modules`);
    return response.data;
  },

  /** Story 5.2: Analyze dependency impact for selected modules */
  analyzeDependencies: async (modules: string[]): Promise<DependencyAnalysisResponse> => {
    const response = await api.post<DependencyAnalysisResponse>(`${BASE}/analyze-dependencies`, {
      modules,
    });
    return response.data;
  },

  /** Story 4.3: Submit opt-in for return notification */
  submitReturnOptIn: async (email: string): Promise<MaintenanceOptInResponse> => {
    const response = await api.post<MaintenanceOptInResponse>(`${BASE}/optin`, { email });
    return response.data;
  },
};
